import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button, Press } from '@dorixona/ui'
import { formatPhone } from '@dorixona/utils'
import { BackButton } from '../../ui/BackButton'
import { Icon } from '../../ui/Icon'
import { Loading, failureText } from '../../ui/Load'
import { Field } from '../auth/ui/Field'
import { DateInput } from '../auth/ui/DateInput'
import { Segmented } from '../auth/ui/Segmented'
import { GENDERS, MAX_BIRTH_DATE, type Gender } from '../auth/model'
import { useSession } from '../../app/session'
import { useLanguage } from '../../app/language'
import { useSaveProfile } from './api'
import { DeleteAccountSheet } from './DeleteAccountSheet'
import { profileView } from './model'
import type { Customer } from '../auth/api'

/**
 * Личные данные: имя, фамилия, пол и дата рождения; номер — на своей странице.
 * Форма появляется, когда профиль уже пришёл: иначе после обновления страницы
 * поля так и остались бы пустыми.
 */
export function EditScreen() {
  const { t } = useTranslation(['account', 'common'])
  const { customer } = useSession()
  if (customer) return <EditForm customer={customer} />
  return (
    <section className="screen cart-wrap">
      <div className="mhead">
        <BackButton to="/profile" />
        <h1>{t('account:edit.title')}</h1>
      </div>
      <Loading what={t('common:state.loading')} count={2} />
    </section>
  )
}

function EditForm({ customer }: { customer: Customer }) {
  const { t } = useTranslation(['account', 'common'])
  const { language } = useLanguage()
  const profile = profileView(customer)
  const save = useSaveProfile()
  const navigate = useNavigate()

  const [deleting, setDeleting] = useState(false)
  const [firstName, setFirstName] = useState(customer.firstName)
  const [lastName, setLastName] = useState(customer.lastName)
  const [gender, setGender] = useState<Gender>(customer.gender === 'female' ? 'female' : 'male')
  const [birthDate, setBirthDate] = useState(customer.birthDate)
  const [message, setMessage] = useState('')

  // Дату шлём, только если её поменяли: ту же самую сервер считает повторной
  // установкой и отказывает (R-56).
  const birthChanged = birthDate !== customer.birthDate

  return (
    <section className="screen cart-wrap">
      <div className="mhead">
        <BackButton to="/profile" />
        <h1>{t('account:edit.title')}</h1>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <Field label={t('account:edit.firstName')}>
          {({ id }) => (
            <input
              id={id}
              autoComplete="given-name"
              value={firstName}
              onChange={(event) => {
                setFirstName(event.target.value)
              }}
            />
          )}
        </Field>
        <Field label={t('account:edit.lastName')}>
          {({ id }) => (
            <input
              id={id}
              autoComplete="family-name"
              value={lastName}
              onChange={(event) => {
                setLastName(event.target.value)
              }}
            />
          )}
        </Field>
        <Field label={t('account:edit.gender')}>
          {() => (
            <Segmented
              label={t('account:edit.gender')}
              value={gender}
              options={GENDERS.map((value) => ({
                value,
                label: t(`account:edit.${value}`),
              }))}
              onChange={setGender}
            />
          )}
        </Field>
        <Field
          label={t('account:edit.birth')}
          optional={t('account:edit.optional')}
          hint={t('account:edit.birthHint')}
        >
          {({ id }) => (
            <DateInput
              id={id}
              max={MAX_BIRTH_DATE}
              value={birthDate}
              onChange={(event) => {
                setBirthDate(event.target.value)
              }}
            />
          )}
        </Field>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <Press
          squish="row"
          className="tx phone-row"
          onPress={() => {
            void navigate('/profile/phone')
          }}
        >
          <div className="ic">
            <Icon name="i-phone" size={18} />
          </div>
          <div>
            <div className="t1 num">{formatPhone(profile.phone)}</div>
            <div className="t2">{t('account:edit.phoneHint')}</div>
          </div>
          {/* Значок вместо подписи — место номеру; что строка меняет номер, говорит подсказка. */}
          <span className="phone-row__act" aria-hidden="true">
            <Icon name="i-edit" size={18} />
          </span>
        </Press>
      </div>

      <Button
        disabled={save.isPending}
        onPress={() => {
          setMessage('')
          save.mutate(
            {
              firstName,
              lastName,
              gender,
              // Язык профиля — тот, на котором покупатель видит приложение.
              lang: language,
              birth: birthChanged ? birthDate : '',
            },
            {
              onSuccess: () => {
                // Сохранили — назад в кабинет: там уже новые имя и данные.
                void navigate('/profile')
              },
              onError: (error) => {
                setMessage(failureText(error, t))
              },
            },
          )
        }}
      >
        {save.isPending ? t('account:edit.saving') : t('common:actions.save')}
      </Button>
      {message ? (
        <p className="auth-note" role="status">
          {message}
        </p>
      ) : null}

      {/* Необратимое — последним и отдельно от «Сохранить», подтверждается шторкой. */}
      <Button
        className="btn-danger-soft"
        aria-haspopup="dialog"
        onPress={() => {
          setDeleting(true)
        }}
      >
        {t('account:delete.title')}
      </Button>
      <DeleteAccountSheet
        opened={deleting}
        onClose={() => {
          setDeleting(false)
        }}
      />
    </section>
  )
}
